import {
  validateProject,
  validateNote,
  validateProjectCreate,
  validateNoteCreate,
  ProjectSchema,
  NoteSchema,
} from '@bower/shared/schemas/validation';
import { stringLengthBounds } from '@bower/shared/zod/introspect';
import { z } from 'zod';

/**
 * Frontend validation utilities
 * Uses centralized validation schemas from shared package
 */

// Type for validation result
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

// Convert Zod validation result to form-friendly format
export const formatZodErrors = (zodError: z.ZodError): Record<string, string> => {
  const errors: Record<string, string> = {};

  zodError.issues.forEach((error) => {
    const path = error.path.join('.');
    errors[path] = error.message;
  });

  return errors;
};

// Project validation helpers
export const validateProjectForm = (data: any): ValidationResult => {
  const result = validateProject(data);

  if (result.success) {
    return { isValid: true, errors: {} };
  }

  return {
    isValid: false,
    errors: formatZodErrors(result.error),
  };
};

export const validateProjectCreateForm = (data: any): ValidationResult => {
  const result = validateProjectCreate(data);

  if (result.success) {
    return { isValid: true, errors: {} };
  }

  return {
    isValid: false,
    errors: formatZodErrors(result.error),
  };
};

// Note validation helpers
export const validateNoteForm = (data: any): ValidationResult => {
  const result = validateNote(data);

  if (result.success) {
    return { isValid: true, errors: {} };
  }

  return {
    isValid: false,
    errors: formatZodErrors(result.error),
  };
};

export const validateNoteCreateForm = (data: any): ValidationResult => {
  const result = validateNoteCreate(data);

  if (result.success) {
    return { isValid: true, errors: {} };
  }

  return {
    isValid: false,
    errors: formatZodErrors(result.error),
  };
};

// Field-level validation helpers
export const validateField = (
  entityType: 'project' | 'note',
  fieldName: string,
  value: any,
): string | null => {
  const schemas = {
    project: ProjectSchema,
    note: NoteSchema,
  };

  const schema = schemas[entityType];
  const fieldSchema = schema.shape[fieldName as keyof typeof schema.shape];

  if (!fieldSchema) {
    return null;
  }

  try {
    fieldSchema.parse(value);
    return null;
  } catch (error) {
    if (error instanceof z.ZodError) {
      return error.issues[0]?.message || 'Invalid value';
    }
    return 'Invalid value';
  }
};

// Get validation rules for a field (for InlineEdit component)
export const getFieldValidationRules = (entityType: 'project' | 'note', fieldName: string) => {
  const schemas = {
    project: ProjectSchema,
    note: NoteSchema,
  };

  const schema = schemas[entityType];
  const fieldSchema = schema.shape[fieldName as keyof typeof schema.shape];

  if (!fieldSchema) {
    return null;
  }

  // Extract validation rules from Zod schema
  const rules: any = {};

  // Check if field is required
  if (!fieldSchema.isOptional()) {
    rules.required = true;
  }

  // Extract string length constraints (through optional/default wrappers)
  const bounds = stringLengthBounds(fieldSchema);
  if (bounds?.min !== undefined) rules.minLength = bounds.min;
  if (bounds?.max !== undefined) rules.maxLength = bounds.max;

  // Extract number constraints (only applies to number fields, not these string/union fields)
  // if (fieldSchema._def.typeName === z.ZodFirstPartyTypeKind.ZodNumber) {
  //   const checks = (fieldSchema._def as any).checks || [];
  //   checks.forEach((check: any) => {
  //     if (check.kind === 'min') {
  //       rules.min = check.value;
  //     }
  //     if (check.kind === 'max') {
  //       rules.max = check.value;
  //     }
  //   });
  // }

  return rules;
};

// Custom validation hook for forms
export const useValidation = (entityType: 'project' | 'note') => {
  const validateForm = (data: any): ValidationResult => {
    switch (entityType) {
      case 'project':
        return validateProjectForm(data);
      case 'note':
        return validateNoteForm(data);
      default:
        return { isValid: false, errors: { general: 'Unknown entity type' } };
    }
  };

  const validateCreateForm = (data: any): ValidationResult => {
    switch (entityType) {
      case 'project':
        return validateProjectCreateForm(data);
      case 'note':
        return validateNoteCreateForm(data);
      default:
        return { isValid: false, errors: { general: 'Unknown entity type' } };
    }
  };

  const validateSingleField = (fieldName: string, value: any): string | null => {
    return validateField(entityType, fieldName, value);
  };

  const getFieldRules = (fieldName: string) => {
    return getFieldValidationRules(entityType, fieldName);
  };

  return {
    validateForm,
    validateCreateForm,
    validateField: validateSingleField,
    getFieldRules,
  };
};
